import * as Api from '@ViewModels';
import { css } from 'aphrodite';
import { inject, observer } from 'mobx-react';
import * as React from 'react';
import { RouteComponentProps, withRouter } from 'react-router-dom';
import { IAdminUserSessionComponentProps } from '../../../../models/AdminModels';
import {
	ErrorMessagesViewModelKey,
	IErrorMessageComponentProps,
	UserSessionViewModelKey,
} from '../../../../models/AppState';
import { IEventLoggingComponentProps, withEventLogging } from '../../../../models/Logging';
import * as AdminViewModels from '../../../../viewmodels/AdminViewModels';
import { LoadingSpinner } from '../../../../web/components/LoadingSpinner';
import { OrderConfirmation } from '../../../../web/components/svgs/graphics/OrderConfirmationGraphic';
import { baseStyleSheet } from '../../../../web/styles/styles';
import { styleSheet } from './styles';

interface IProps
	extends RouteComponentProps<any>,
		IErrorMessageComponentProps,
		IAdminUserSessionComponentProps,
		IEventLoggingComponentProps {}

const _AccountConfirmation = (props: IProps) => {
	const { userSession } = props;

	const [account, setAccount] = React.useState<Api.IAccount>(null);

	const [scheduleMeetingUrl, setScheduleMeetingUrl] = React.useState<string>(null);
	const [showLoader, setShowLoader] = React.useState<boolean>(true);
	const [label, setLabel] = React.useState<string>('');

	const accountId = props?.match?.params?.id;
	const mAccountOperations = React.useMemo(() => {
		return new AdminViewModels.AccountOperationsViewModel(userSession, { id: accountId });
	}, [accountId, userSession]);

	React.useEffect(() => {
		if (!account) {
			mAccountOperations.load().then((x: Api.IAccount) => {
				const savedAccount = JSON.parse(JSON.stringify(x));
				/** Save account info */
				setAccount(savedAccount);
				/** GET initial meeting team */
				mAccountOperations
					.getInitialMeetingTeam(savedAccount)

					.then((res: Api.IAccountTeam) => {
						/** Save team name */
						setLabel(res.label.toLocaleLowerCase());
						/** POST to get the schedule meeting url */
						mAccountOperations
							.getScheduleMeetingUrl(savedAccount, res.name)
							.then(r => {
								if (r) {
									setScheduleMeetingUrl(r.url);
									setShowLoader(false);
								}
							})
							.catch(err => {
								setShowLoader(false);
								throw err;
							});
					})
					.catch(err => {
						setShowLoader(false);
						throw err;
					});
			});
		}

		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [account]);

	const onSkip = () => {
		navigateToAccount();
	};

	const onScheduleMeeting = () => {
		if (!scheduleMeetingUrl) {
			return;
		}

		navigateToAccount();
		window.open(scheduleMeetingUrl, '_blank');
	};
	const LoaderIndicator = () => {
		return showLoader ? <LoadingSpinner type='large' /> : null;
	};

	function navigateToAccount() {
		props.history.push(`/accounts/${accountId}`);
	}

	if (!account) {
		return (
			<div className={css(styleSheet.container)}>
				<LoaderIndicator />
			</div>
		);
	}
	return (
		<>
			<section className={css(styleSheet.container)}>
				<h1>Order Confirmation</h1>
				<figure>
					<OrderConfirmation />
				</figure>
				<h2 className={css(styleSheet.heading)}>Setup Complete!</h2>
				<p className={css(styleSheet.paragraph)}>
					{`You have successfully created an account for `}
					<strong className={css(styleSheet.strong)}>{account.companyName}</strong>.
				</p>
				<p className={css(styleSheet.paragraph)}>{`As a next step let's set up a meeting for ${label}.`}</p>
				{scheduleMeetingUrl ? (
					<footer className={css(styleSheet.footer)}>
						<button className={css(baseStyleSheet.ctaButton)} onClick={onScheduleMeeting}>
							{`Schedule ${label} meeting`}
						</button>{' '}
						<button className={css(baseStyleSheet.ctaButtonReverse)} onClick={onSkip}>
							Skip for now
						</button>
					</footer>
				) : (
					<LoadingSpinner type='small' />
				)}
			</section>
		</>
	);
};

const AccountConfirmationAsObserver = observer(_AccountConfirmation);
const AccountConfirmationWithContext = withRouter(
	inject(ErrorMessagesViewModelKey, UserSessionViewModelKey)(AccountConfirmationAsObserver)
);
export const AccountConfirmation = withEventLogging(AccountConfirmationWithContext, 'AccountConfirmation');
